// Which industry this page belongs to, read from the path first, then the
// subdomain.
//
//   demodashboard.jalgroup.id/pmb/insight  -> pmb (path tenant)
//   financial.localhost:3000               -> financial (subdomain tenant)
//   fmcg.localhost:3000/insight            -> fmcg
//   localhost:3000                         -> DEFAULT_INDUSTRY (owner's call)
//   demodashboard.jalgroup.id              -> DEFAULT_INDUSTRY
//   unknown.localhost:3000                 -> null, the app shows "industri tidak dikenal"
//
// The path form exists because a deployed demo lives on one host
// (demodashboard.jalgroup.id): a wildcard certificate covers one label, so
// telco.demodashboard.jalgroup.id cannot be served. Browsers resolve every
// *.localhost name to the loopback address, so subdomains keep working in
// development with no hosts file.
import { DEFAULT_INDUSTRY, INDUSTRY_SLUGS, type IndustrySlug } from "@dashboard-intelligent/api/src/modules/industry/types";

export function isIndustrySlug(value: string): value is IndustrySlug {
  return (INDUSTRY_SLUGS as readonly string[]).includes(value);
}

/**
 * The industry for a hostname. A bare host (no subdomain, or an IP address)
 * opens the default industry. On localhost a subdomain that is not a known
 * slug is null; on a deployed domain the first label is the app's own name
 * (demodashboard.jalgroup.id), so it opens the default industry.
 */
export function industryFromHost(hostname: string): IndustrySlug | null {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith("[")) return DEFAULT_INDUSTRY;
  const labels = host.split(".");
  // "localhost" alone, or a two-label apex like "example.com", has no subdomain.
  const isLocal = labels[labels.length - 1] === "localhost";
  const subdomainCount = labels.length - (isLocal ? 1 : 2);
  if (subdomainCount <= 0) return DEFAULT_INDUSTRY;
  const first = labels[0] ?? "";
  if (isIndustrySlug(first)) return first;
  return isLocal ? null : DEFAULT_INDUSTRY;
}

/** The industry named by the first path segment, e.g. "/pmb/insight" -> "pmb". */
export function industryFromPath(pathname: string): IndustrySlug | null {
  const first = pathname.split("/")[1]?.toLowerCase() ?? "";
  return isIndustrySlug(first) ? first : null;
}

export interface Tenant {
  slug: IndustrySlug | null;
  /** "/pmb" when the industry came from the path, "" otherwise. */
  prefix: string;
}

/** The tenant for a location: a path segment wins over the subdomain. */
export function tenantFrom(current: { hostname: string; pathname: string }): Tenant {
  const fromPath = industryFromPath(current.pathname);
  if (fromPath) return { slug: fromPath, prefix: `/${fromPath}` };
  return { slug: industryFromHost(current.hostname), prefix: "" };
}

/** The path with any industry prefix removed: "/pmb/insight" -> "/insight". */
export function stripTenant(pathname: string): string {
  const slug = industryFromPath(pathname);
  if (!slug) return pathname;
  return pathname.slice(slug.length + 1) || "/";
}

/** The current tenant prefix, read from the live location. */
export function currentPrefix(): string {
  return typeof location === "undefined" ? "" : tenantFrom(location).prefix;
}

/** A page path inside the current tenant: "/insight" -> "/pmb/insight". */
export function tenantPath(path: string): string {
  const prefix = currentPrefix();
  if (!prefix) return path;
  return path === "/" ? prefix : `${prefix}${path}`;
}

/** The base host with any industry subdomain removed, e.g. "localhost" or "example.com". */
export function baseHost(hostname: string): string {
  const labels = hostname.toLowerCase().split(".");
  return labels.length > 1 && isIndustrySlug(labels[0] ?? "") ? labels.slice(1).join(".") : hostname.toLowerCase();
}

/**
 * The path of the same page in another industry, on this same host. Every
 * host serves every industry through the path form, so a switch is a
 * same-origin route change, never a jump to another origin.
 */
export function industryPath(slug: IndustrySlug, pathname: string): string {
  const page = stripTenant(pathname);
  return page === "/" ? `/${slug}` : `/${slug}${page}`;
}

/** The URL of the same page in another industry: same origin, path form. */
export function industryUrl(slug: IndustrySlug, current: { protocol: string; hostname: string; port: string; pathname: string }): string {
  const port = current.port ? `:${current.port}` : "";
  return `${current.protocol}//${baseHost(current.hostname)}${port}${industryPath(slug, current.pathname)}`;
}
